import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import Client from '@/api/Client';
import { checkError } from '@/api/Error';

interface User {
  id: number | null;
  role: string | null;
  email: string | null;
  avatar_url: string | null;
  first_name: string | null;
  last_name: string | null;
  phone: string | null;
  orders: any[];
  pinnedCount: number;
  currency_decimals: number;
  currency_symbol: string;
  currency_code: string;
  agency_ids: number[];
  two_factor_enabled?: boolean;
}

export type CreateSessionResult =
  | { status: 'authenticated' }
  | { status: 'need_two_factor'; challenge: string };

interface AuthState {
  token: string | null;
  user: User;
  touch: boolean;
  setToken: (token: string | null) => void;
  setUser: (user: Partial<User>) => void;
  setTouch: (touch: boolean) => void;
  createSession: (email: string, password: string) => Promise<CreateSessionResult>;
  completeTwoFactorSession: (challenge: string, otpCode: string) => Promise<void>;
  fetchManagerProfile: () => Promise<void>;
  destroySession: () => Promise<void>;
  deleteUser: () => void;
  resetState: () => void;
  fetchPinnedOrders: (per?: number) => Promise<void>;
  addPinnedOrder: (order: any) => Promise<void>;
  removePinnedOrder: (order: any) => Promise<void>;
}

const initialUser: User = {
  id: null,
  role: null,
  email: null,
  avatar_url: null,
  first_name: null,
  last_name: null,
  phone: null,
  orders: [],
  pinnedCount: 0,
  currency_decimals: 2,
  currency_symbol: '€',
  currency_code: 'EUR',
  agency_ids: [],
};

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      token: null,
      user: initialUser,
      touch: false,

      setToken: (token) => set({ token }),
      setUser: (user) => set((state) => ({ user: { ...state.user, ...user } })),
      setTouch: (touch) => set({ touch }),
      resetState: () => {
        set({ token: null, user: initialUser });
        localStorage.removeItem('runnrz-admin-storage');
      },

      createSession: async (email: string, password: string) => {
        const client = new Client();
        const req = await client.request(null, 'post', 'session', {
          session: { email, password },
        });
        if (req.data?.two_factor_required && req.data?.challenge) {
          return { status: 'need_two_factor', challenge: req.data.challenge as string };
        }
        if (req.status === 201 && req.data?.session?.authentication_token) {
          set({ token: req.data.session.authentication_token });
          set({ user: { ...initialUser, ...req.data.manager } });
          return { status: 'authenticated' };
        }
        throw new Error('Réponse de connexion inattendue');
      },

      completeTwoFactorSession: async (challenge: string, otpCode: string) => {
        const client = new Client();
        const req = await client.request(null, 'post', 'session/verify_two_factor', {
          two_factor: { challenge, otp_code: otpCode },
        });
        if (req.status === 201 && req.data?.session?.authentication_token) {
          set({ token: req.data.session.authentication_token });
          set({ user: { ...initialUser, ...req.data.manager } });
          return;
        }
        throw new Error('Réponse 2FA inattendue');
      },

      fetchManagerProfile: async () => {
        const { token, user } = get();
        if (!token || user.id == null) return;
        const client = new Client();
        const req = await client.request(token, 'get', `managers/${user.id}`);
        if (req.data?.manager) {
          set((state) => ({
            user: { ...state.user, ...req.data.manager },
          }));
        }
      },

      destroySession: async () => {
        try {
          const { token } = get();
          const client = new Client();
          await client.request(token, 'delete', 'session');
          get().resetState();
        } catch (error: any) {
          console.error(error);
          get().resetState();
        }
      },

      deleteUser: () => {
        get().resetState();
      },

      fetchPinnedOrders: async (per?: number) => {
        try {
          const { token } = get();
          const client = new Client();
          const req = await client.request(token, 'get', 'orders', null, {
            pinned: true,
            per,
          });
          if (req.data.orders.length) {
            set((state) => ({
              user: {
                ...state.user,
                orders: req.data.orders,
                pinnedCount: req.data.pagination.total_count,
              },
            }));
          }
        } catch (error: any) {
          checkError(error);
        }
      },

      addPinnedOrder: async (order: any) => {
        try {
          const { token } = get();
          const client = new Client();
          await client.request(token, 'post', `orders/${order.id}/pin`);
          set((state) => ({
            user: {
              ...state.user,
              orders: [...state.user.orders, order],
              pinnedCount: state.user.pinnedCount + 1,
            },
          }));
        } catch (error: any) {
          checkError(error);
        }
      },

      removePinnedOrder: async (order: any) => {
        try {
          const { token } = get();
          const client = new Client();
          await client.request(token, 'delete', `orders/${order.id}/pin`);
          set((state) => ({
            user: {
              ...state.user,
              orders: state.user.orders.filter((o) => o.id !== order.id),
              pinnedCount: state.user.pinnedCount - 1,
            },
          }));
        } catch (error: any) {
          checkError(error);
        }
      },
    }),
    {
      name: 'runnrz-admin-storage',
      storage: createJSONStorage(() => localStorage),
    }
  )
);
